import { createContext, ReactNode, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';

type RemunerationCategory = 'PRODUCTION' | 'COMMISSION' | 'BONUS';
type CrewRole = 'MESTRE' | 'MOTORISTA' | 'COZINHEIRO' | 'GELADOR' | 'PESCADOR';
type CompensationSpecies = 'CAMARAO' | 'GO' | 'PIRAMUTABA';
type TripFormation = 'INDIVIDUAL' | 'PARELHA' | 'TRILHEIRA';
type BaseType = 'NONE' | 'ROUND_AVERAGE_WEIGHT' | 'SELECTED_BOATS_WEIGHT' | 'TRIP_WEIGHT' | 'TRIP_PRODUCTION' | 'TARGET_SPECIES_WEIGHT' | 'BYCATCH_WEIGHT';
type CalculationType = 'FIXED_AMOUNT' | 'WEIGHT_SHARE_PRICE_PER_KG' | 'MASTER_FORMATION_PRICE_PER_KG' | 'PRICE_PER_KG' | 'WEIGHT_TIER_FIXED_AMOUNT';

export interface RemunerationRuleComponentInput {
  base_type: BaseType;
  calculation_type: CalculationType;
  parameters: Record<string, number>;
  boat_ids: string[];
  tiers: Array<{
    minimum_weight: number;
    maximum_weight: number | null;
    fixed_amount: number;
  }>;
}

export interface RemunerationRuleInput {
  name?: string;
  category?: RemunerationCategory;
  valid_from: string;
  valid_to: string | null;
  employee_id: string | null;
  crew_role: CrewRole | null;
  compensation_species: CompensationSpecies | null;
  trip_formation: TripFormation | null;
  components: RemunerationRuleComponentInput[];
}

interface RemunerationRulesRequest {
  page?: number;
  pageSize?: number;
}

interface ResponseProps {
  success: boolean;
  message: string;
  data?: any;
}

interface RemunerationRulesContextData {
  getRemunerationRules: (request: RemunerationRulesRequest) => Promise<ResponseProps>;
  getRemunerationRuleById: (id: string) => Promise<ResponseProps>;
  createRemunerationRule: (rule: RemunerationRuleInput) => Promise<ResponseProps>;
  createRemunerationRuleVersion: (id: string, rule: RemunerationRuleInput) => Promise<ResponseProps>;
  updateRemunerationRule: (id: string, rule: Pick<RemunerationRuleInput, 'name'>) => Promise<ResponseProps>;
  updateRemunerationRuleVersion: (id: string, versionId: string, rule: RemunerationRuleInput) => Promise<ResponseProps>;
  removeRemunerationRule: (id: string) => Promise<ResponseProps>;
  removeRemunerationRuleVersion: (id: string, versionId: string) => Promise<ResponseProps>;
}

interface RemunerationRulesProviderProps {
  children: ReactNode;
}

const RemunerationRulesContext = createContext<RemunerationRulesContextData>({} as RemunerationRulesContextData);
const remunerationRulesQueryKey = ['remuneration-rules'];

function getApiMessage(response: any) {
  return response?.data?.message || response?.message || response?.data?.errors?.message || response?.data?.errors?.toString?.();
}

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

export function translateKnownRemunerationRuleApiMessage(message: unknown, validationMessage = 'Não foi possível validar os dados da regra. Revise as informações e tente novamente.') {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^validation failed\.?$/i.test(normalizedMessage)) return validationMessage;
  if (/remuneration rule not found/i.test(normalizedMessage)) return 'Regra de remuneração não encontrada ou não está mais disponível.';
  if (/valid_to must be on or after valid_from/i.test(normalizedMessage)) return 'A validade final deve ser igual ou posterior à validade inicial.';
  if (/production requires a crew role/i.test(normalizedMessage)) return 'Regras de produção precisam de uma função na tripulação.';
  if (/commission requires an employee/i.test(normalizedMessage)) return 'Regras de comissão precisam de um tripulante.';
  if (/bonus requires GELADOR and compensation species/i.test(normalizedMessage)) return 'Regras de bonificação precisam ser para Gelador e ter espécie para remuneração.';
  if (/selected boats are required for this base/i.test(normalizedMessage)) return 'Selecione pelo menos um barco quando a base for peso dos barcos selecionados.';
  if (/a boat cannot be repeated in the same component/i.test(normalizedMessage)) return 'Um barco não pode ser repetido no mesmo componente.';
  if (/weight tiers require tiers/i.test(normalizedMessage)) return 'As faixas de peso precisam de pelo menos uma faixa cadastrada.';
  if (/tiers must be contiguous, non-overlapping intervals/i.test(normalizedMessage)) return 'As faixas devem ser intervalos contínuos e sem sobreposição.';
  if (/this version overlaps an existing rule/i.test(normalizedMessage)) return 'Esta versão conflita com uma regra existente no mesmo contexto de validade.';
  if (/a replacement version must start after the version it replaces/i.test(normalizedMessage)) return 'A nova versão deve começar depois da versão que ela substitui. Para manter a mesma data inicial, edite a versão atual.';
  if (/this version already has a replacement/i.test(normalizedMessage)) return 'Esta versão já possui uma substituta. Edite ou reverta a versão de rascunho mais recente.';
  if (/this version has a later replacement and cannot be edited/i.test(normalizedMessage)) return 'Esta versão possui uma substituta mais recente e não pode ser editada. Edite ou reverta a versão de rascunho mais recente.';
  if (/this remuneration rule has already been used/i.test(normalizedMessage)) return 'Esta regra já foi usada em uma geração e não pode ser alterada ou removida.';
  if (/this remuneration rule version has already been used/i.test(normalizedMessage)) return 'Esta versão já foi usada em uma geração e não pode ser alterada ou removida. Crie uma nova versão.';
  if (/the only version cannot be deleted/i.test(normalizedMessage)) return 'A única versão da regra não pode ser removida separadamente. Remova a regra inteira.';
  if (/remuneration rule version not found/i.test(normalizedMessage)) return 'Versão da regra de remuneração não encontrada ou não está mais disponível.';
  if (/this version has a later replacement and cannot be deleted/i.test(normalizedMessage)) return 'Esta versão possui uma substituta mais recente e não pode ser revertida.';
  if (/the version being replaced is no longer available/i.test(normalizedMessage)) return 'A versão anterior não está mais disponível para restauração.';
  if (/employee is invalid for this company/i.test(normalizedMessage)) return 'O tripulante informado não pertence a esta empresa ou está inativo.';
  if (/one or more boats are invalid for this company/i.test(normalizedMessage)) return 'Um ou mais barcos selecionados não pertencem a esta empresa ou estão inativos.';
  if (/factor must be positive/i.test(normalizedMessage)) return 'Informe um fator de formação maior que zero.';
  if (/pricePerKg must be positive/i.test(normalizedMessage)) return 'Informe um preço por kg maior que zero.';
  if (/totalShares must be positive/i.test(normalizedMessage)) return 'Informe uma quantidade total de cotas maior que zero.';
  if (/employeeShares must be positive/i.test(normalizedMessage)) return 'Informe a quantidade de cotas do tripulante maior que zero.';
  if (/amount must be positive/i.test(normalizedMessage)) return 'Informe um valor fixo maior que zero.';
  if (/fixed amount calculations require the NONE base/i.test(normalizedMessage)) return 'Cálculos de valor fixo precisam usar a opção Sem base de cálculo.';
  if (/the NONE base can only be used with fixed amount calculations/i.test(normalizedMessage)) return 'A opção Sem base de cálculo só pode ser usada com cálculos de valor fixo.';
  if (/(page|pagesize).*(greater than|less than|min|max|integer|number)|number must be (greater than or equal to|less than or equal to)/i.test(normalizedMessage)) return 'Informe uma página e uma quantidade por página válidas.';
  if (/invalid uuid|expected uuid/i.test(normalizedMessage)) return 'Informe identificadores válidos para a regra, tripulante e barcos.';
  if (/expected a valid date in yyyy-mm-dd format|invalid date/i.test(normalizedMessage)) return 'Informe as datas no formato AAAA-MM-DD.';
  if (/invalid enum value|crew_role|category|base_type|calculation_type|compensation_species|trip_formation/i.test(normalizedMessage)) return 'Informe opções válidas para categoria, contexto e cálculo da regra.';
  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) return 'Você não tem permissão para realizar esta ação.';
  if (/^unauthorized\.?$/i.test(normalizedMessage)) return 'Sua sessão expirou. Faça login novamente.';

  return undefined;
}

function normalizeMessage(response: any, fallback: string, validationMessage?: string) {
  const apiMessage = getApiMessage(response);
  return translateKnownRemunerationRuleApiMessage(apiMessage, validationMessage) || (isTechnicalMessage(apiMessage) ? fallback : apiMessage);
}

function removeEmptyFilters(request: RemunerationRulesRequest) {
  return Object.entries(request).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') acc[key] = value;
    return acc;
  }, {});
}

function normalizeRulePayload(rule: RemunerationRuleInput) {
  return {
    ...(rule.name !== undefined ? { name: rule.name.trim() } : {}),
    ...(rule.category !== undefined ? { category: rule.category } : {}),
    valid_from: rule.valid_from,
    valid_to: rule.valid_to || null,
    employee_id: rule.employee_id || null,
    crew_role: rule.crew_role || null,
    compensation_species: rule.compensation_species || null,
    trip_formation: rule.trip_formation || null,
    components: rule.components.map((component) => ({
      base_type: component.base_type,
      calculation_type: component.calculation_type,
      parameters: component.parameters,
      boat_ids: component.boat_ids || [],
      tiers: component.tiers || []
    }))
  };
}

export function RemunerationRulesProvider({ children }: RemunerationRulesProviderProps) {
  const queryClient = useQueryClient();

  function invalidateRemunerationRules() {
    queryClient.invalidateQueries({ queryKey: remunerationRulesQueryKey });
  }

  async function getRemunerationRules(request: RemunerationRulesRequest = {}) {
    const response: any = await apiV2.get('/remuneration-rules', {
      params: removeEmptyFilters(request)
    });
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) return { success: true, message: '', data: response.data };

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao consultar as regras de remuneração.'),
      data: { data: [], pagination: { totalPages: 1 } }
    };
  }

  async function getRemunerationRuleById(id: string) {
    const response: any = await apiV2.get(`/remuneration-rules/${id}`);
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) return { success: true, message: '', data: response.data };

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao consultar a regra de remuneração.', 'Não foi possível validar a regra informada. Revise o identificador e tente novamente.'),
      data: null
    };
  }

  async function createRemunerationRule(rule: RemunerationRuleInput) {
    const response: any = await apiV2.post('/remuneration-rules', normalizeRulePayload(rule));
    if (response.status === 201) {
      invalidateRemunerationRules();
      return { success: true, message: 'Regra de remuneração adicionada com sucesso!', data: response.data };
    }

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao adicionar regra de remuneração.')
    };
  }

  async function createRemunerationRuleVersion(id: string, rule: RemunerationRuleInput) {
    const response: any = await apiV2.post(`/remuneration-rules/${id}/versions`, normalizeRulePayload(rule));
    if (response.status === 201) {
      invalidateRemunerationRules();
      return { success: true, message: 'Nova versão da regra de remuneração adicionada com sucesso!', data: response.data };
    }

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao criar nova versão da regra de remuneração.')
    };
  }

  async function updateRemunerationRule(id: string, rule: Pick<RemunerationRuleInput, 'name'>) {
    const response: any = await apiV2.put(`/remuneration-rules/${id}`, { name: rule.name?.trim() });
    if (response.status >= 200 && response.status < 300) {
      invalidateRemunerationRules();
      return { success: true, message: 'Regra de remuneração atualizada com sucesso!', data: response.data };
    }

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao atualizar regra de remuneração.')
    };
  }

  async function updateRemunerationRuleVersion(id: string, versionId: string, rule: RemunerationRuleInput) {
    const response: any = await apiV2.put(`/remuneration-rules/${id}/versions/${versionId}`, normalizeRulePayload(rule));
    if (response.status >= 200 && response.status < 300) {
      invalidateRemunerationRules();
      return { success: true, message: 'Versão da regra de remuneração atualizada com sucesso!', data: response.data };
    }

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao atualizar versão da regra de remuneração.')
    };
  }

  async function removeRemunerationRule(id: string) {
    const response: any = await apiV2.delete(`/remuneration-rules/${id}`);
    if (response.status >= 200 && response.status < 300) {
      invalidateRemunerationRules();
      return { success: true, message: 'Regra de remuneração removida com sucesso!', data: response.data };
    }

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao remover regra de remuneração.')
    };
  }

  async function removeRemunerationRuleVersion(id: string, versionId: string) {
    const response: any = await apiV2.delete(`/remuneration-rules/${id}/versions/${versionId}`);
    if (response.status >= 200 && response.status < 300) {
      invalidateRemunerationRules();
      return { success: true, message: 'Versão revertida com sucesso!', data: response.data };
    }

    return {
      success: false,
      message: normalizeMessage(response, 'Erro ao reverter versão da regra de remuneração.')
    };
  }

  return (
    <RemunerationRulesContext.Provider value={{ getRemunerationRules, getRemunerationRuleById, createRemunerationRule, createRemunerationRuleVersion, updateRemunerationRule, updateRemunerationRuleVersion, removeRemunerationRule, removeRemunerationRuleVersion }}>
      {children}
    </RemunerationRulesContext.Provider>
  );
}

export function UseRemunerationRules() {
  return useContext(RemunerationRulesContext);
}
